import { useSettings } from '@/db/hooks'
import { PageTitle } from '@/components/ui'
import { patchSettings } from '@/lib/settingsWrite'
import { LearnTabs } from './parts'
import { DEFAULT_TIMER_S } from './DeckView'

const switchClass = (on: boolean) =>
  `relative w-12 h-7 rounded-full transition-colors shrink-0 ${on ? 'bg-accent' : 'bg-line-strong'}`

const Switch = ({
  on,
  onChange,
  label,
  testId,
}: {
  on: boolean
  onChange: (on: boolean) => void
  label: string
  testId: string
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    className={switchClass(on)}
    onClick={() => onChange(!on)}
    data-testid={testId}
  >
    <span
      className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-on-accent transition-transform ${on ? 'translate-x-5' : ''}`}
    />
  </button>
)

/** Learn's own settings: the daily deck's push and the quick pick's timer. */
export const LearnSettingsView = () => {
  const settings = useSettings()
  const learn = settings.learn ?? {}
  const pushOn = !learn.push_off
  const timerS = learn.timer_s ?? DEFAULT_TIMER_S
  const save = (patch: Partial<NonNullable<typeof settings.learn>>) =>
    void patchSettings({ learn: { ...learn, ...patch } })
  return (
    <div data-testid="learn-settings">
      <PageTitle>Learn settings</PageTitle>
      <LearnTabs />
      <ul className="group-list mt-2">
        <li className="flex items-center justify-between gap-3 px-3.5 py-3 min-h-14">
          <span>
            <span className="block text-[15px] font-medium">Daily deck reminder</span>
            <span className="block text-sm text-muted">
              One quiet nudge a day. Skipped when you have already done a deck.
            </span>
          </span>
          <Switch
            on={pushOn}
            onChange={(on) => save({ push_off: on ? undefined : true })}
            label="Daily deck reminder"
            testId="learn-push-switch"
          />
        </li>
        {pushOn && (
          <li className="flex items-center justify-between gap-3 px-3.5 py-3 min-h-14">
            <span className="text-[15px]">At</span>
            <input
              type="time"
              defaultValue={learn.push_at ?? '09:30'}
              key={`lp-${learn.push_at}`}
              onBlur={(e) => e.target.value && save({ push_at: e.target.value })}
              className="w-32"
              data-testid="learn-push-time"
            />
          </li>
        )}
        <li className="flex items-center justify-between gap-3 px-3.5 py-3 min-h-14">
          <span>
            <span className="block text-[15px] font-medium">Quick pick timer</span>
            <span className="block text-sm text-muted">
              A soft countdown. It never counts against you: the answer shows and the card comes back.
            </span>
          </span>
          <Switch
            on={timerS > 0}
            onChange={(on) => save({ timer_s: on ? DEFAULT_TIMER_S : 0 })}
            label="Quick pick timer"
            testId="learn-timer-switch"
          />
        </li>
      </ul>
    </div>
  )
}
